import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Possibly the ugliest python ever to escape my brain",
  "description": "",
  "published": "2012-02-13T00:00:00.000Z",
  "redirect_from": ["/blog/possibly-the-ugliest-python-ever-to-escape-my-brain/swizec/3661"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Lensing `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Data",
        "title": "Data"
      }}>{`data`}</a>{` from one format to another always produces the most horrible code ... here's some I wrote last night:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`    # some other code here

    def stitch(*iterables):
        return [(iterables[0][i][0], sum([l[i][1] for l in iterables]))
                for i in xrange(len(iterables[0]))]

    def one(z):
        d = dict(stitch(*[[b for b in zip(a.keys(), a.values()) if b[0] in ['count', 'step_conv_ratio', 'overall_conv_ratio']] for a in z]))

        d['overall_conv_ratio'] = d['overall_conv_ratio']/len(funnel['meta']['dates'])
        d['step_conv_ratio'] = d['step_conv_ratio']/len(funnel['meta']['dates'])
        return d

    print map(one,
              zip(*[v['steps'][:entry['counts']['p']] for v in funnel['data'].values()]))
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/Funnel_of_the_Mauretania3.jpg",
        "alt": "The funnel of the Mauretania.",
        "title": "The funnel of the Mauretania."
      }}></img></p>
    <p>{`Can you guess what all of that does? Me neither, let me try to explain.`}</p>
    <h2 {...{
      "id": "wtf"
    }}>{`WTF!`}</h2>
    <p>{`Firstly, the basic problem I'm trying to solve is fetching some funnel data from `}<a parentName="p" {...{
        "href": "http://www.mixpanel.com",
        "title": "Mixpanel"
      }}>{`Mixpanel`}</a>{`, which returns all the data I need, but it is split into different days and I'd really like to have it all mashed together. Due to the design of funnels in their `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Application_programming_interface",
        "title": "Application programming interface"
      }}>{`API`}</a>{`, they also return too much data, which spoils what I'm looking for.`}</p>
    <p>{`Their format is something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{'Signup flow': {'data': {'2010-05-24': {'analysis': {'completion': 0.064679359580052493,
                                               'starting_amount': 762,
                                               'steps': 3,
                                               'worst': 2},
                                  'steps': [{'count': 762,
                                             'goal': 'pages',
                                             'overall_conv_ratio': 1.0,
                                             'step_conv_ratio': 1.0},
                                            {'count': 69,
                                             'goal': 'View signup',
                                             'overall_conv_ratio': 0.09055118110236221,
                                             'step_conv_ratio': 0.09055118110236221},
                                            // etc.},
                   '2010-05-31': // etc.
          'meta': {'dates': ['2010-05-24', '2010-05-31']}}}
`}</code></pre>
    <p>{`What I want is something where "Signup flow" would be just a list of steps where count is a sum of all counts for that step and conversion ratios are the average between all days. Yes this discards some useful data, but it's not too relevant for what I'm trying to analyze.`}</p>
    <h2 {...{
      "id": "justified"
    }}>{`Justified?`}</h2>
    <p>{`Hopefully I can defend this code, explain why it's ugly.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`map(one,
    zip(*[v['steps'][:entry['counts']['p']] for v in funnel['data'].values()]))
`}</code></pre>
    <p>{`This code takes the data for each day, iterates over it to take only the `}<em parentName="p">{`steps`}</em>{` key, cuts its list at an appropriate length (the funnel's got 60+ steps, I have extra data to tell me how much is actually applicable). Then zips all the steps together.`}</p>
    <p>{`Now we have a list of step `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Tuple",
        "title": "Tuple"
      }}>{`tuples`}</a>{` - all the first steps, second steps and so on.`}</p>
    <p>{`Then we apply the one function on each tuple:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def one(z):
        d = dict(stitch(*[[b for b in zip(a.keys(), a.values()) if b[0] in ['count', 'step_conv_ratio', 'overall_conv_ratio']] for a in z]))

        d['overall_conv_ratio'] = d['overall_conv_ratio']/len(funnel['meta']['dates'])
        d['step_conv_ratio'] = d['step_conv_ratio']/len(funnel['meta']['dates'])
        return d
`}</code></pre>
    <p>{`Right, so the first thing that happens is we turn a dictionary into a list of `}<em parentName="p">{`(key, value)`}</em>{` tuples, then this is filtered so only the ones we're actually interested in remain (count and the conversion ratios).`}</p>
    <p>{`These are then stitched together:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def stitch(*iterables):
        return [(iterables[0][i][0], sum([l[i][1] for l in iterables]))
                for i in xrange(len(iterables[0]))]
`}</code></pre>
    <p>{`Looks pretty bad, but really just turns a list of `}<em parentName="p">{`(key, value)`}</em>{` tuples into a list of `}<em parentName="p">{`(key, sum of corresponding values)`}</em>{` tuples.`}</p>
    <p>{`Finally, the conversion values are turned into the average by simply dividing with the number of days this funnel was tracked for.`}</p>
    <h2 {...{
      "id": "better-way"
    }}>{`Better way?`}</h2>
    <p>{`This is the cleanest and most elegant way I could come up with to solve this problem - there's bound to be something cleaner and easier. Actually, `}<em parentName="p">{`please`}</em>{` tell me there's a better way!`}</p>
    <p>{`Generally speaking, is there even a good approach to take for converting data returned from somewhere into the kind of data you need somewhere else? Or are we doomed to forever spend most of our time figuring out how to get API's talking to one another?`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://usapartisan.com/2012/02/07/a-quality-assessment-of-us-jobs-reveals-the-ugliest-picture-yet/"
        }}>{`A "Quality Assessment" Of US Jobs Reveals The Ugliest Picture Yet`}</a>{` (usapartisan.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8542010/ironpython-lists-tuples-dictionaries-crash-wcf-communications"
        }}>{`IronPython Lists, Tuples, Dictionaries crash WCF communications`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8875195/python-entry-widget-tuple-combo"
        }}>{`python entry widget tuple combo`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8658995/recursively-create-tuples-from-lists"
        }}>{`recursively create tuples from lists`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8107254/regarding-list-of-tuples-and-file"
        }}>{`Regarding list of tuples and file`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://socialmediacorporatesolutions.wordpress.com/2012/02/13/feeding-the-funnel-with-facebook/"
        }}>{`Feeding The Funnel With Facebook`}</a>{` (socialmediacorporatesolutions.wordpress.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=2991a8b4-c6f1-4e7d-b19f-d376285fcee7",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      